import { Check, Minus } from 'lucide-react';
import { foundation } from '../../ui';
import { useIsTouch } from '../../hooks/ui/useIsTouch';
import { touchTapAreaProps } from '../../ui/touchTapArea';

const fc = foundation.color;

/**
 * Size presets. `md` is the default bulk-select box (customer row, admin
 * Customers list). `sm` is the lighter per-job box used inside an expanded
 * Ready-to-Bill customer so the row-level (customer) checkbox stays the
 * visually dominant control and the per-job opt-outs read as secondary.
 * Mobile bumps the wrapper for a touch target without growing the glyph.
 * KAN-947: on touch the wrapper also carries the invisible 44 x 44 tap area (the 24px
 * desktop wrapper on a tablet / sideways iPad, the 36px `sm` wrapper on a phone) — the
 * drawing and the layout never change.
 */
const SIZE_PRESETS = {
  md: { box: 18, icon: 12, wrap: 24, wrapMobile: 44 },
  sm: { box: 15, icon: 10, wrap: 22, wrapMobile: 36 },
};

/**
 * Shared selectable checkbox used by bulk-select surfaces (billing Ready
 * queue + admin Customers list). Visually consistent across both: rounded
 * square with the brand color fill + a Lucide `Check` glyph when selected
 * (or a `Minus` dash when `indeterminate`). Mobile bumps the wrapper so the
 * hit area meets touch targets without enlarging the visual box.
 *
 * Renders semantically as a `role="checkbox"` span (not a native input)
 * because we want full styling control + an icon. Keyboard support:
 * Space/Enter toggles, Tab focus.
 *
 * @param {object} props
 * @param {boolean} props.checked
 * @param {boolean} [props.indeterminate] - render the dash (mixed) state; takes visual precedence over `checked`
 * @param {() => void} props.onChange
 * @param {string} props.ariaLabel
 * @param {boolean} [props.isMobile]
 * @param {boolean} [props.disabled]
 * @param {'sm'|'md'} [props.size]
 * @param {boolean} [props.strongBorder] - unchecked border uses `borderStrong` (tinted surfaces)
 */
export default function SelectableCheckbox({
  checked,
  indeterminate = false,
  onChange,
  ariaLabel,
  isMobile = false,
  disabled = false,
  size = 'md',
  // Visual-only: renders JUST the box glyph (no role / tabIndex / handlers, no
  // touch hit-area) so it can be nested INSIDE a larger clickable control —
  // e.g. the export CategoryCard, which is itself the `role="checkbox"` button
  // that owns the toggle + aria-checked. Keeps the canonical box in one place.
  presentational = false,
  // Unchecked border = `borderStrong` instead of `border` — for a box sitting
  // on a TINTED surface (e.g. the slate-100 recovery-codes ack row), where
  // the default border all but disappears. White surfaces keep the default.
  strongBorder = false,
}) {
  const isTouch = useIsTouch();
  const dims = SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const wrap = isMobile ? dims.wrapMobile : dims.wrap;
  const filled = checked || indeterminate;

  const boxGlyph = (
    <span style={{
      width: dims.box,
      height: dims.box,
      flexShrink: 0,
      borderRadius: 4,
      border: `1.5px solid ${filled ? fc.brand.primary : (strongBorder ? fc.neutral.borderStrong : fc.neutral.border)}`,
      backgroundColor: filled ? fc.brand.primary : fc.neutral.surface,
      color: fc.brand.onPrimary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      transition: 'background-color 120ms ease, border-color 120ms ease',
    }}>
      {indeterminate
        ? <Minus size={dims.icon} strokeWidth={3} aria-hidden />
        : (checked && <Check size={dims.icon} strokeWidth={3} aria-hidden />)}
    </span>
  );

  if (presentational) {
    return (
      <span aria-hidden style={{ display: 'inline-flex', pointerEvents: 'none', opacity: disabled ? 0.5 : 1 }}>
        {boxGlyph}
      </span>
    );
  }

  const handle = (e) => {
    if (disabled) return;
    e.stopPropagation();
    onChange?.();
  };

  return (
    <span
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : !!checked}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      {...touchTapAreaProps(isTouch || isMobile)}
      tabIndex={disabled ? -1 : (isMobile ? -1 : 0)}
      onClick={handle}
      onMouseDown={(e) => {
        // Keep tap from focusing the checkbox — mobile browsers scroll focused
        // targets into view and the list jumps.
        e.preventDefault();
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          handle(e);
        }
      }}
      style={{
        width: wrap,
        height: wrap,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        WebkitTapHighlightColor: 'transparent',
        touchAction: isMobile ? 'pan-y' : 'manipulation',
      }}
    >
      {boxGlyph}
    </span>
  );
}
